import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import axios from 'axios';
import { ContextData } from '../../context/Context';
import baseUrl from '../../BaseURL';
import './Login.css';

export default function Login() {
  const { completeLogin } = useContext(ContextData);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const controller = useRef(null);

  useEffect(() => () => controller.current?.abort(), []);

  async function submit(event) {
    event.preventDefault();
    if (inFlight.current) return;
    if (!email.trim() || !password.trim()) {
      setError('Enter your email address and password to continue.');
      return;
    }
    inFlight.current = true;
    controller.current = new AbortController();
    const request = controller.current;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${baseUrl}/free/admin_u_login`, { email: email.trim(), passwrd: password }, {
        withCredentials: true, headers: { Accept: 'application/json' }, timeout: 15000, signal: request.signal
      });
      if (request.signal.aborted) return;
      if (!data?.status) {
        setError(typeof data?.text === 'string' ? data.text : 'Sign-in was unsuccessful. Check your email and password.');
        return;
      }
      if (typeof data.accesstoken !== 'string' || !data.accesstoken || typeof data.token !== 'string' || !data.token) {
        setError('Your sign-in could not be completed. Please try again.');
        return;
      }
      completeLogin(data);
      navigate(data.userType === 'Administrator' ? '/dashboard/statistics' : '/dashboard/members', { replace: true });
    } catch (err) {
      if (request.signal.aborted) return;
      setError(typeof err.response?.data?.text === 'string' ? err.response.data.text
        : typeof err.response?.data?.message === 'string' ? err.response.data.message
          : 'We could not connect to your account. Please check your connection and try again.');
    } finally {
      inFlight.current = false;
      if (!request.signal.aborted) setBusy(false);
    }
  }

  return <main className="fin-signin">
    <section className="fin-signin-story" aria-label="Fityanul Islam of Nigeria">
      <Link to="/home" className="fin-signin-brand" aria-label="Fityanul Islam of Nigeria home">
        <span className="fin-signin-emblem"><img src="/assets/fit.jpeg" alt="" width="56" height="56" /></span>
        <span>FITYANUL ISLAM<small>OF NIGERIA</small></span>
      </Link>
      <div className="fin-signin-story-copy">
        <span className="fin-signin-eyebrow">ONE COMMUNITY. SHARED PURPOSE.</span>
        <h2>Connected by faith.{' '}<br /><em>United in service.</em></h2>
        <p className="fin-signin-arabic" lang="ar" dir="rtl">فتيان الإسلام في جمهورية نيجيريا</p>
        <p className="fin-signin-story-description">A dedicated space for our people, our records and the work that brings us together.</p>
        <div className="fin-signin-values"><span>Knowledge</span><span>Unity</span><span>Service</span></div>
      </div>
      <div className="fin-signin-story-footer"><span>FITYANUL ISLAM OF NIGERIA</span><span>RC: 8847</span></div>
      <div className="fin-signin-orbit" aria-hidden="true" />
    </section>

    <section className="fin-signin-access" aria-labelledby="fin-signin-title">
      <Link className="fin-signin-back" to="/home"><span aria-hidden="true">←</span> Back to home</Link>
      <div className="fin-signin-form-wrap">
        <span className="fin-signin-portal-label"><span aria-hidden="true" /> MEMBERSHIP PORTAL</span>
        <h1 id="fin-signin-title">Welcome back.</h1>
        <p className="fin-signin-intro">Sign in to manage your membership records and continue where you left off.</p>
        <form onSubmit={submit} aria-label="Sign in" aria-busy={busy}>
          {error && <div className="fin-signin-error" role="alert" id="fin-signin-error">{error}</div>}
          <div className="fin-signin-field">
            <label htmlFor="fin-signin-email">Email address</label>
            <input id="fin-signin-email" name="email" type="email" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false} placeholder="Enter your email address" value={email} onChange={event => { setEmail(event.target.value); setError(''); }} required disabled={busy} aria-describedby={error ? 'fin-signin-error' : undefined} />
          </div>
          <div className="fin-signin-field">
            <label htmlFor="fin-signin-password">Password</label>
            <div className="fin-signin-password">
              <input id="fin-signin-password" name="password" type={visible ? 'text' : 'password'} autoComplete="current-password" placeholder="Enter your password" value={password} onChange={event => { setPassword(event.target.value); setError(''); }} required disabled={busy} aria-describedby={error ? 'fin-signin-error' : undefined} />
              <button type="button" aria-label={visible ? 'Hide password' : 'Show password'} aria-pressed={visible} aria-controls="fin-signin-password" onClick={() => setVisible(value => !value)} disabled={busy}>{visible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}</button>
            </div>
          </div>
          <button className="fin-signin-submit" type="submit" disabled={busy}>{busy ? <><span className="fin-signin-spinner" aria-hidden="true" /> Signing in…</> : <>Sign in <ArrowForwardOutlinedIcon /></>}</button>
          <p className="fin-signin-help">Need access or help signing in?<br /><strong>Contact your account administrator.</strong><br /><Link to="/register-user">Apply for a registration account</Link></p>
        </form>
      </div>
      <footer className="fin-signin-footer">© {new Date().getFullYear()} Fityanul Islam of Nigeria<span>Faith. Knowledge. Service to humanity.</span></footer>
    </section>
  </main>;
}
